/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Offcanvas } from 'react-bootstrap';
import {
  FiCheckCircle,
  FiXCircle,
  FiCalendar,
  FiClock,
  FiShield,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';
import { formatEntry, formatVisaType } from '../../utils/onlineVisaUtils';
import './VisaDetailsModal.scss';

const titleCase = (str) => {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
};

function RequirementRow({ label, required }) {
  return (
    <li className="visa-details-modal__requirement-row">
      <span className="visa-details-modal__requirement-label">{label}</span>
      <span
        className={`visa-details-modal__requirement-badge visa-details-modal__requirement-badge--${
          required ? 'yes' : 'no'
        }`}
      >
        {required ? <FiCheckCircle /> : <FiXCircle />}
        <span>{required ? 'Required' : 'Not Required'}</span>
      </span>
    </li>
  );
}

RequirementRow.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
};

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <li className={`visa-details-modal__faq ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className="visa-details-modal__faq-question"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>{q}</span>
        {open ? <FiChevronUp /> : <FiChevronDown />}
      </button>
      {open && (
        <div className="visa-details-modal__faq-answer">
          <p>{a}</p>
        </div>
      )}
    </li>
  );
}

FaqItem.propTypes = {
  q: PropTypes.string.isRequired,
  a: PropTypes.string.isRequired,
};

function VisaDetailsModal({ show, onHide, visaDetail, loading, error }) {
  const info = visaDetail?.visaDetailInfo || {};
  const destination =
    visaDetail?.destination?.destinationName ||
    visaDetail?.destination?.destinationCode ||
    '';
  const visaName = useMemo(() => {
    if (visaDetail?.visaName) return visaDetail.visaName;
    const entry = formatEntry(visaDetail?.entry || '');
    const duration = visaDetail?.durationDisplay
      ? `${visaDetail.durationDisplay} ${(
          visaDetail.durationType || ''
        ).toLowerCase()}`.trim()
      : '';
    const parts = [titleCase(destination), entry, duration].filter(Boolean);
    return parts.join(' ');
  }, [visaDetail, destination]);

  const guidelines = Array.isArray(info.travelGuidelines)
    ? info.travelGuidelines
    : [];
  const faqs = Array.isArray(info.questionAndAnswer)
    ? info.questionAndAnswer
    : [];

  const infoGrid = [
    {
      key: 'validity',
      label: 'Validity',
      value:
        visaDetail?.validityDisplay && visaDetail.validityDisplay.trim()
          ? `${visaDetail.validityDisplay.trim()} ${
              visaDetail.validityType || ''
            }`.trim()
          : '-',
      icon: <FiCalendar />,
    },
    {
      key: 'stay',
      label: 'Stay Duration',
      value:
        visaDetail?.durationDisplay && visaDetail.durationDisplay.trim()
          ? `${visaDetail.durationDisplay.trim()} ${
              visaDetail.durationType || ''
            }`.trim()
          : '-',
      icon: <FiClock />,
    },
    {
      key: 'processing',
      label: 'Processing Time',
      value: visaDetail?.processingTime || info.turnAroundTime || '-',
      icon: <FiClock />,
    },
    {
      key: 'applyBefore',
      label: 'Apply Before',
      value: info.minApplyBeforeTravel || '-',
      icon: <FiShield />,
    },
  ];

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="visa-details-modal"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="visa-details-modal__title">
          Visa Details
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body className="visa-details-modal__body">
        {loading && (
          <div className="visa-details-modal__loader">
            <div className="visa-details-modal__skeleton visa-details-modal__skeleton--lg" />
            <div className="visa-details-modal__skeleton" />
            <div className="visa-details-modal__skeleton" />
            <div className="visa-details-modal__skeleton" />
          </div>
        )}

        {!loading && error && (
          <div className="visa-details-modal__error">{error}</div>
        )}

        {!loading && !error && visaDetail && (
          <>
            {/* Chips row */}
            <div className="visa-details-modal__chips">
              {visaDetail.visaType && (
                <span className="visa-details-modal__chip">
                  {formatVisaType(visaDetail.visaType)}
                </span>
              )}
              {visaDetail.entry && (
                <span className="visa-details-modal__chip visa-details-modal__chip--muted">
                  {(formatEntry(visaDetail.entry) || '').toUpperCase()}
                </span>
              )}
            </div>

            {/* Heading */}
            <h2 className="visa-details-modal__heading">
              {visaName || 'Visa details'}
            </h2>

            {/* 2x2 info grid */}
            <div className="visa-details-modal__grid">
              {infoGrid.map((item) => (
                <div className="visa-details-modal__grid-item" key={item.key}>
                  <span className="visa-details-modal__grid-icon">
                    {item.icon}
                  </span>
                  <div className="visa-details-modal__grid-text">
                    <span className="visa-details-modal__grid-label">
                      {item.label}
                    </span>
                    <span className="visa-details-modal__grid-value">
                      {item.value}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Requirements */}
            {/* <section className="visa-details-modal__section">
              <h3 className="visa-details-modal__section-title">
                Requirements
              </h3>
              <ul className="visa-details-modal__requirement-list">
                <RequirementRow
                  label="Biometric"
                  required={!!info.biometricRequired}
                />
                <RequirementRow
                  label="Interview"
                  required={!!info.interviewRequirement}
                />
                <RequirementRow
                  label="Original Documents"
                  required={!!info.originalDocumentsRequired}
                />
              </ul>
            </section> */}

            {/* Travel Guidelines */}
            {guidelines.length > 0 && (
              <section className="visa-details-modal__section">
                <h3 className="visa-details-modal__section-title">
                  Travel Guidelines
                </h3>
                <ul className="visa-details-modal__guidelines">
                  {guidelines.map((g, idx) => (
                    <li
                      // eslint-disable-next-line react/no-array-index-key
                      key={idx}
                      className="visa-details-modal__guideline-item"
                    >
                      {typeof g === 'string'
                        ? g
                        : g?.text || g?.guideline || ''}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* FAQs */}
            {faqs.length > 0 && (
              <section className="visa-details-modal__section">
                <h3 className="visa-details-modal__section-title">FAQs</h3>
                <ul className="visa-details-modal__faqs">
                  {faqs.map((item, idx) => {
                    const q = item?.question || item?.q || '';
                    const a = item?.answer || item?.a || '';
                    if (!q && !a) return null;
                    return (
                      <FaqItem
                        // eslint-disable-next-line react/no-array-index-key
                        key={idx}
                        q={q}
                        a={a}
                      />
                    );
                  })}
                </ul>
              </section>
            )}
          </>
        )}

        {!loading && !error && !visaDetail && (
          <div className="visa-details-modal__empty">
            No visa details available.
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}

VisaDetailsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  visaDetail: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

VisaDetailsModal.defaultProps = {
  visaDetail: null,
  loading: false,
  error: '',
};

export default VisaDetailsModal;
